import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { Eye } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import type { BadgeTone } from "@/components/admin/StatusBadge";
import { StatusBadge } from "@/components/admin/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminScope } from "@/contexts/AdminScopeContext";
import { useAdminReviews } from "@/hooks/admin/reviews";
import { AdminLayout } from "@/layouts/AdminLayout";
import type { AdminReviewListItem } from "@/types";

const PAGE_SIZE = 20;

// Backend sortable keys for the admin reviews list; restrict TanStack sorting to these.
const SORTABLE = new Set([
  "username",
  "topic_title",
  "ai_score",
  "admin_score",
  "revision_number",
  "review_status",
  "submitted_at",
]);

const STATUS_META: Record<string, { label: string; tone: BadgeTone }> = {
  pending: { label: "Pending", tone: "neutral" },
  failed: { label: "Failed", tone: "danger" },
  ai_reviewed: { label: "AI Reviewed", tone: "info" },
  submitted_for_review: { label: "Awaiting Review", tone: "warning" },
  admin_confirmed: { label: "Reviewed", tone: "success" },
  admin_updated: { label: "Updated", tone: "success" },
};

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AdminReviewsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [sorting, setSorting] = useState<SortingState>([{ id: "submitted_at", desc: true }]);

  // Cohort + W#A# slot come from the shared admin scope (topbar switcher).
  const { programmeId, week, assignment } = useAdminScope();

  // Reset pagination when the scope changes (render-time state adjustment,
  // per the React "state derived from props" pattern — no effect needed).
  const scopeKey = `${programmeId}:${week}:${assignment}`;
  const [lastScopeKey, setLastScopeKey] = useState(scopeKey);
  if (scopeKey !== lastScopeKey) {
    setLastScopeKey(scopeKey);
    setPage(1);
  }

  const sort = sorting[0];
  const reviewsQuery = useAdminReviews({
    page,
    page_size: PAGE_SIZE,
    sort_by: sort?.id ?? "submitted_at",
    sort_order: sort ? (sort.desc ? "desc" : "asc") : "desc",
    q: search || undefined,
    status: statusFilter === "ALL" ? undefined : statusFilter,
    programme_id: programmeId ?? undefined,
    week_number: week ?? undefined,
    assignment_number: assignment ?? undefined,
  });
  const data = reviewsQuery.data;
  const isLoading = reviewsQuery.isPending;
  const error = reviewsQuery.isError
    ? reviewsQuery.error instanceof Error
      ? reviewsQuery.error.message
      : "Failed to load reviews"
    : null;
  const { refetch } = reviewsQuery;

  const columns = useMemo<ColumnDef<AdminReviewListItem>[]>(
    () => [
      {
        accessorKey: "username",
        header: "User",
        cell: ({ row }) => (
          <span className="font-medium text-gray-900">{row.original.username}</span>
        ),
      },
      {
        accessorKey: "assignment_number",
        header: "Assignment",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            W{row.original.week_number}A{row.original.assignment_number}
          </span>
        ),
      },
      {
        accessorKey: "topic_title",
        header: "Topic",
        cell: ({ row }) => (
          <span className="block max-w-[200px] truncate text-sm text-gray-700">
            {row.original.topic_title}
          </span>
        ),
      },
      {
        accessorKey: "ai_score",
        header: "AI Score",
        cell: ({ row }) => (
          <span className="font-medium text-blue-600">{row.original.ai_score}</span>
        ),
      },
      {
        accessorKey: "admin_score",
        header: "Final Score",
        cell: ({ row }) =>
          row.original.admin_score != null ? (
            <span className="font-medium text-green-600">{row.original.admin_score}</span>
          ) : (
            <span className="text-gray-300">—</span>
          ),
      },
      {
        accessorKey: "revision_number",
        header: "Rev",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">{row.original.revision_number}</span>
        ),
      },
      {
        accessorKey: "review_status",
        header: "Status",
        cell: ({ row }) => {
          const meta = STATUS_META[row.original.review_status] ?? {
            label: row.original.review_status,
            tone: "neutral" as BadgeTone,
          };
          return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
        },
      },
      {
        accessorKey: "submitted_at",
        header: "Submitted",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">{formatDate(row.original.submitted_at)}</span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            aria-label="View review"
            onClick={() => navigate(`/admin/reviews/${row.original.review_id}`)}
            className="text-gray-400 transition-colors hover:text-gray-700"
          >
            <Eye className="h-4 w-4" />
          </button>
        ),
      },
    ],
    [navigate]
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const valid = next.filter((s) => SORTABLE.has(s.id));
      setPage(1);
      return valid.length ? valid : prev;
    });
  };

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <DataTable
          className="h-full"
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          emptyMessage="No reviews match the current filters"
          getRowId={(r) => r.review_id}
          toolbar={
            <div className="flex items-center justify-between gap-3">
              <SearchInput
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
                placeholder="Search by user or topic…"
              />
              <div className="flex items-center gap-2">
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All statuses</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="submitted_for_review">Awaiting Review</SelectItem>
                    <SelectItem value="ai_reviewed">AI Reviewed</SelectItem>
                    <SelectItem value="admin_confirmed">Reviewed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>
    </AdminLayout>
  );
}
